import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AcademyQnaChatState,
  initialAcademyQnaChatState,
} from './entities/academy-qna-chat-store.model';
import type { Guid, LiveQuestionStatusType } from './entities/academy-enums.model';
import { LiveQuestionsService } from './infra/live-questions.service';
import { LiveChatService } from './infra/live-chat.service';
import { LiveEventsService } from './infra/live-events.service';
import { AcademyRealtimeStore } from './academy-realtime.store';

const ALL_OPS = [
  'ask',
  'upvote',
  'setStatus',
  'loadQuestions',
  'loadHistory',
  'loadProducerQuestions',
  'loadProducerHistory',
  'loadMuted',
  'loadReactions',
  'removeMessage',
  'mute',
  'unmute',
] as const;

/** Producer studio chat backlog size — same depth as the viewer history read (`take` 100). */
const PRODUCER_CHAT_PAGE_SIZE = 100;

export const AcademyQnaChatStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyQnaChatState>(initialAcademyQnaChatState),
  withReqState(...ALL_OPS),
  withMethods(
    (
      store,
      questions = inject(LiveQuestionsService),
      chat = inject(LiveChatService),
      liveEvents = inject(LiveEventsService),
      errorStore = inject(ErrorStore),
      realtime = inject(AcademyRealtimeStore),
    ) => {
      const isGatedDenial = (error: HttpErrorResponse): boolean =>
        error.status === 403 || error.status === 404;

      const ask = rxMethod<{ liveEventId: Guid; content: string }>(
        pipe(
          tap(() => patchState(store, setPropLoading('ask'))),
          switchMap(({ liveEventId, content }) =>
            questions.ask({ LiveEventId: liveEventId, Content: content }).pipe(
              tapResponse({
                next: (question) =>
                  patchState(
                    store,
                    {
                      questionsByEvent: {
                        ...store.questionsByEvent(),
                        [liveEventId]: [
                          ...(store.questionsByEvent()[liveEventId] ?? []),
                          question,
                        ],
                      },
                    },
                    setPropLoaded('ask'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('ask', error));
                },
              }),
            ),
          ),
        ),
      );

      const upvote = rxMethod<{ liveEventId: Guid; questionId: Guid }>(
        pipe(
          tap(() => patchState(store, setPropLoading('upvote'))),
          switchMap(({ liveEventId, questionId }) =>
            questions.upvote(questionId).pipe(
              tapResponse({
                next: (res) =>
                  patchState(
                    store,
                    {
                      questionsByEvent: {
                        ...store.questionsByEvent(),
                        [liveEventId]: (store.questionsByEvent()[liveEventId] ?? []).map(
                          (q) =>
                            q.LiveQuestionId === questionId
                              ? { ...q, UpvoteCount: res.UpvoteCount }
                              : q,
                        ),
                      },
                    },
                    setPropLoaded('upvote'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('upvote', error));
                },
              }),
            ),
          ),
        ),
      );

      const setStatus = rxMethod<{
        liveEventId: Guid;
        questionId: Guid;
        status: 'Answered' | 'Dismissed';
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('setStatus'))),
          switchMap(({ liveEventId, questionId, status }) =>
            questions.setStatus(questionId, status).pipe(
              tapResponse({
                next: () =>
                  patchState(
                    store,
                    {
                      questionsByEvent: {
                        ...store.questionsByEvent(),
                        [liveEventId]: (store.questionsByEvent()[liveEventId] ?? []).map(
                          (q) =>
                            q.LiveQuestionId === questionId
                              ? {
                                  ...q,
                                  LiveQuestionStatusType:
                                    status as LiveQuestionStatusType,
                                }
                              : q,
                        ),
                      },
                      producerQuestionsByEvent: {
                        ...store.producerQuestionsByEvent(),
                        [liveEventId]: (
                          store.producerQuestionsByEvent()[liveEventId] ?? []
                        ).map((q) =>
                          q.LiveQuestionId === questionId
                            ? { ...q, LiveQuestionStatusType: status as LiveQuestionStatusType }
                            : q,
                        ),
                      },
                    },
                    setPropLoaded('setStatus'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('setStatus', error));
                },
              }),
            ),
          ),
        ),
      );

      const loadQuestions = rxMethod<Guid>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadQuestions'))),
          switchMap((liveEventId) =>
            questions.list(liveEventId).pipe(
              tapResponse({
                next: (list) =>
                  patchState(
                    store,
                    {
                      questionsByEvent: {
                        ...store.questionsByEvent(),
                        [liveEventId]: list,
                      },
                    },
                    setPropLoaded('loadQuestions'),
                  ),
                error: (error: HttpErrorResponse) => {
                  // 403 (outside the audience) and 404 (live not visible) are the expected
                  // answers of this gated read, and the page did it alone: no toast.
                  if (!isGatedDenial(error)) errorStore.setHttpError(error);
                  patchState(store, setPropError('loadQuestions', error));
                },
              }),
            ),
          ),
        ),
      );

      const loadHistory = rxMethod<{ liveEventId: Guid; take?: number }>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadHistory'))),
          switchMap(({ liveEventId, take }) =>
            chat.history(liveEventId, take).pipe(
              tapResponse({
                next: (messages) =>
                  patchState(
                    store,
                    {
                      // The read is newest first; the panel lists oldest → newest, with live
                      // hub messages appended below.
                      chatByEvent: {
                        ...store.chatByEvent(),
                        [liveEventId]: [...messages].reverse(),
                      },
                    },
                    setPropLoaded('loadHistory'),
                  ),
                error: (error: HttpErrorResponse) => {
                  // Gated like `loadQuestions`: 403 and 404 are expected, and silent.
                  if (!isGatedDenial(error)) errorStore.setHttpError(error);
                  patchState(store, setPropError('loadHistory', error));
                },
              }),
            ),
          ),
        ),
      );

      /**
       * Producer studio question backlog — `GET /LiveQuestions/producer`: every tenant's
       * questions, each with the asker's `PersonName` (backend 013d).
       *
       * Landing this read is what lets the hub store drop the vote/status values it recorded
       * before the studio's re-read went out (`producerQuestionsRead`): only this store knows
       * the read arrived, and both writes happen in the same synchronous step, so the panel
       * never renders the read with the superseded values still applied over it.
       */
      const loadProducerQuestions = rxMethod<Guid>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadProducerQuestions'))),
          switchMap((liveEventId) =>
            questions.listForProducer(liveEventId).pipe(
              tapResponse({
                next: (list) => {
                  patchState(
                    store,
                    {
                      producerQuestionsByEvent: {
                        ...store.producerQuestionsByEvent(),
                        [liveEventId]: list,
                      },
                    },
                    setPropLoaded('loadProducerQuestions'),
                  );
                  realtime.producerQuestionsRead(liveEventId, list);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadProducerQuestions', error));
                },
              }),
            ),
          ),
        ),
      );

      /**
       * Producer studio chat history — `GET /LiveEvents/{id}/chat`: every tenant's messages,
       * newest first, with `PersonName` and the moderation `IsRemoved` flag (backend 013c/d).
       */
      const loadProducerHistory = rxMethod<Guid>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadProducerHistory'))),
          switchMap((liveEventId) =>
            liveEvents.chat(liveEventId, { top: PRODUCER_CHAT_PAGE_SIZE }).pipe(
              tapResponse({
                next: (page) =>
                  patchState(
                    store,
                    {
                      // `/chat` is newest first; the panel lists oldest → newest, with live hub
                      // messages appended below.
                      producerChatByEvent: {
                        ...store.producerChatByEvent(),
                        [liveEventId]: [...page.items].reverse(),
                      },
                    },
                    setPropLoaded('loadProducerHistory'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadProducerHistory', error));
                },
              }),
            ),
          ),
        ),
      );

      /**
       * Who is muted in the chat — `GET /LiveEvents/{id}/chat/muted` (backend round 24): what
       * lets a studio that just opened, or another producer's, show who is muted without having
       * been the one who clicked.
       *
       * Landing this read is what lets the hub store drop the mute changes it had recorded
       * when the read went out (`mutedRead`): the read carries those, and applying them over it
       * would bring back a mute another producer undid while this connection was down.
       *
       * 403 and 404 stay silent: the studio reads this alone when it opens, and a service that
       * has not received round 24 yet answers 404 to the route.
       */
      const loadMuted = rxMethod<Guid>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadMuted'))),
          switchMap((liveEventId) => {
            const seenChanges = realtime.muteChangeCount();
            return chat.muted(liveEventId).pipe(
              tapResponse({
                next: (muted) => {
                  patchState(
                    store,
                    { mutedByEvent: { ...store.mutedByEvent(), [liveEventId]: muted } },
                    setPropLoaded('loadMuted'),
                  );
                  realtime.mutedRead(liveEventId, seenChanges);
                },
                error: (error: HttpErrorResponse) => {
                  if (!isGatedDenial(error)) errorStore.setHttpError(error);
                  patchState(store, setPropError('loadMuted', error));
                },
              }),
            );
          }),
        ),
      );

      const loadReactions = rxMethod<Guid>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadReactions'))),
          switchMap((liveEventId) =>
            chat.reactions(liveEventId).pipe(
              tapResponse({
                next: (counts) =>
                  patchState(
                    store,
                    {
                      reactionsByEvent: {
                        ...store.reactionsByEvent(),
                        [liveEventId]: counts,
                      },
                    },
                    setPropLoaded('loadReactions'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadReactions', error));
                },
              }),
            ),
          ),
        ),
      );

      const removeMessage = rxMethod<{ liveEventId: Guid; messageId: Guid }>(
        pipe(
          tap(() => patchState(store, setPropLoading('removeMessage'))),
          switchMap(({ liveEventId, messageId }) =>
            chat.removeMessage(liveEventId, messageId).pipe(
              tapResponse({
                next: () =>
                  patchState(
                    store,
                    {
                      chatByEvent: {
                        ...store.chatByEvent(),
                        [liveEventId]: (store.chatByEvent()[liveEventId] ?? []).filter(
                          (m) => m.ChatMessageId !== messageId,
                        ),
                      },
                      producerChatByEvent: {
                        ...store.producerChatByEvent(),
                        [liveEventId]: (store.producerChatByEvent()[liveEventId] ?? []).filter(
                          (m) => m.ChatMessageId !== messageId,
                        ),
                      },
                    },
                    setPropLoaded('removeMessage'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removeMessage', error));
                },
              }),
            ),
          ),
        ),
      );

      const mute = rxMethod<{ liveEventId: Guid; personId: Guid }>(
        pipe(
          tap(() => patchState(store, setPropLoading('mute'))),
          switchMap(({ liveEventId, personId }) =>
            chat.mute(liveEventId, personId).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('mute')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('mute', error));
                },
              }),
            ),
          ),
        ),
      );

      const unmute = rxMethod<{ liveEventId: Guid; personId: Guid }>(
        pipe(
          tap(() => patchState(store, setPropLoading('unmute'))),
          switchMap(({ liveEventId, personId }) =>
            chat.unmute(liveEventId, personId).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('unmute')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('unmute', error));
                },
              }),
            ),
          ),
        ),
      );

      /**
       * Drops the producer studio backlogs of an event (every tenant's questions and chat, and
       * who is muted) so they never show on a viewer page of the same live later in the session.
       */
      const clearProducerBacklog = (liveEventId: Guid): void => {
        const questionsLeft = { ...store.producerQuestionsByEvent() };
        const chatLeft = { ...store.producerChatByEvent() };
        const mutedLeft = { ...store.mutedByEvent() };
        delete questionsLeft[liveEventId];
        delete chatLeft[liveEventId];
        delete mutedLeft[liveEventId];
        patchState(store, {
          producerQuestionsByEvent: questionsLeft,
          producerChatByEvent: chatLeft,
          mutedByEvent: mutedLeft,
        });
      };

      const reset = (): void =>
        patchState(store, initialAcademyQnaChatState, setPropInit(...ALL_OPS));

      return {
        ask,
        upvote,
        setStatus,
        loadQuestions,
        loadHistory,
        loadProducerQuestions,
        loadProducerHistory,
        loadMuted,
        clearProducerBacklog,
        loadReactions,
        removeMessage,
        mute,
        unmute,
        reset,
      };
    },
  ),
);
